/**
 * RecordingSettingsPage — self-serve reseller-level recording configuration.
 *
 * Reseller owners don't have access to the /resellers/:id page (that's a
 * super_admin surface), but they DO need to manage their own reseller's
 * recording dial-in number + recycle bin retention/pricing. This page is
 * the reseller-owner-accessible mirror of the cards on ResellerDetail.
 *
 * For super_admin users we redirect to the full reseller detail page
 * instead — that view shows ALL reseller-level config in one place.
 */
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { resellers } from '@/api/resellers';
import { PageHeader } from '@/components/PageHeader';
import { useAuth } from '@/lib/auth-store';
import {
  RecordingDialInCard,
  RecycleBinConfigCard,
} from '../resellers/ResellerDetail';

export function RecordingSettingsPage() {
  const user = useAuth((s) => s.user);
  const tenant = useAuth((s) => s.tenant);

  // Super_admin doesn't have a single "my reseller" — they manage many.
  // Send them back to the full list so they pick which reseller to edit.
  if (user?.is_super_admin || user?.role === 'super_admin') {
    return <Navigate to="/resellers" replace />;
  }

  // Reseller_owner — the user's own resellerId comes from the tenant scope.
  const resellerId = tenant?.resellerId ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['reseller', resellerId],
    queryFn: () => (resellerId ? resellers.get(resellerId) : Promise.reject(new Error('no_reseller_scope'))),
    enabled: Boolean(resellerId),
  });

  if (!resellerId) {
    return (
      <div className="text-sm text-destructive">
        No reseller scope on your account. Ask the platform owner to set
        your reseller assignment.
      </div>
    );
  }
  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (error || !data) {
    return (
      <div className="text-sm text-destructive">
        {error instanceof Error ? error.message : 'Failed to load reseller settings'}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Recording configuration"
        description={
          'Set the phone number admins call to record audio for your customers, and how long deleted recordings stay recoverable in the recycle bin.'
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        <RecordingDialInCard
          resellerId={data.id}
          current={data.recording_dial_in_e164 ?? null}
        />
        <RecycleBinConfigCard reseller={data} />
      </div>
    </>
  );
}
